import { motion } from "framer-motion";
import {
  Monitor,
  Smartphone,
  LayoutDashboard,
  Server,
  Shield,
  Zap,
  Box,
  Database,
  HardDrive,
  Cloud,
} from "lucide-react";

/* ─── animation variants ─── */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

/* ─── animated connection line with traveling pulse ─── */
function FlowLine({ delay = 0 }: { delay?: number }) {
  return (
    <motion.div
      variants={fadeUp}
      className="flex flex-col items-center py-1"
    >
      <svg width="2" height="48" viewBox="0 0 2 48" className="overflow-visible">
        {/* Static line */}
        <line
          x1="1" y1="0" x2="1" y2="48"
          stroke="var(--bg-border)"
          strokeWidth="1.5"
        />
        {/* Traveling pulse */}
        <motion.circle
          cx="1"
          r="2.5"
          fill="#ea580c"
          filter="url(#pulseGlow)"
          initial={{ cy: 0, opacity: 0 }}
          animate={{ cy: [0, 48], opacity: [0, 1, 1, 0] }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: delay,
            repeatDelay: 0.6,
          }}
        />
        <defs>
          <filter id="pulseGlow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>
    </motion.div>
  );
}

/* ─── reusable mini-card ─── */
function MiniCard({
  icon: Icon,
  label,
  accent = false,
}: {
  icon: React.ElementType;
  label: string;
  accent?: boolean;
}) {
  return (
    <motion.div
      variants={scaleIn}
      whileHover={{ y: -3, transition: { duration: 0.25 } }}
      className={`
        group relative flex flex-col items-center gap-2.5 px-5 py-4 rounded-xl
        border transition-all duration-300 cursor-default
        ${accent
          ? "border-primary/30 bg-primary/5 hover:border-primary/50 hover:bg-primary/10"
          : "border-[var(--bg-border)] bg-[var(--bg-surface)] hover:border-primary/30 hover:bg-primary/5"
        }
      `}
    >
      <div className={`
        flex items-center justify-center h-9 w-9 rounded-lg transition-colors duration-300
        ${accent
          ? "bg-primary/15 group-hover:bg-primary/25"
          : "bg-white/5 group-hover:bg-primary/15"
        }
      `}>
        <Icon className={`h-[18px] w-[18px] transition-colors duration-300 ${accent ? "text-primary" : "text-[var(--text-secondary)] group-hover:text-primary"}`} />
      </div>
      <span className="text-xs font-medium text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors duration-300 whitespace-nowrap">
        {label}
      </span>
    </motion.div>
  );
}

/* ─── storage item ─── */
function StorageItem({ icon: Icon, label }: { icon: React.ElementType; label: string }) {
  return (
    <motion.div
      variants={scaleIn}
      whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
      className="flex flex-col items-center gap-2 p-3 rounded-lg bg-[var(--bg-void)] border border-[var(--bg-border)] hover:border-primary/25 transition-all duration-300 cursor-default"
    >
      <Icon className="h-[18px] w-[18px] text-[var(--text-secondary)]" />
      <span className="text-[11px] text-[var(--text-secondary)] text-center leading-tight">
        {label}
      </span>
    </motion.div>
  );
}

/* ─── section label ─── */
function SectionLabel({ children, glow = false }: { children: string; glow?: boolean }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      {glow && (
        <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_2px_rgba(234,88,12,0.5)] animate-pulse" />
      )}
      <span className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-[0.15em] font-mono">
        {children}
      </span>
    </div>
  );
}

/* ─── main component ─── */
export default function AnimatedArchitecture() {
  return (
    <div className="w-full max-w-4xl mx-auto rounded-2xl border border-[var(--bg-border)] bg-[var(--bg-surface)] relative overflow-hidden">
      {/* Dot grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className="relative z-10 p-8 sm:p-10 flex flex-col items-center"
      >
        {/* ── CLIENT LAYER ── */}
        <motion.div variants={fadeUp} className="w-full">
          <SectionLabel>Client Layer</SectionLabel>
          <div className="flex flex-wrap justify-center gap-3">
            <MiniCard icon={Monitor} label="Web App" />
            <MiniCard icon={Smartphone} label="Mobile App" />
            <MiniCard icon={LayoutDashboard} label="Admin UI" />
          </div>
        </motion.div>

        {/* Flow line */}
        <FlowLine delay={0} />

        {/* ── SERVER LAYER ── */}
        <motion.div variants={fadeUp} className="w-full">
          <SectionLabel glow>Server Layer (Express)</SectionLabel>
          <div className="relative rounded-xl border border-primary/20 bg-primary/[0.03] p-5 overflow-hidden">
            {/* Subtle inner glow */}
            <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />

            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniCard icon={Server} label="HTTP Server" accent />
              <MiniCard icon={Shield} label="Auth & Rules" accent />
              <MiniCard icon={Zap} label="Realtime Broker" accent />
              <MiniCard icon={Box} label="JSVM Hooks" accent />
            </div>
          </div>
        </motion.div>

        {/* Flow line */}
        <FlowLine delay={0.9} />

        {/* ── CORE ENGINE & STORAGE ── */}
        <motion.div
          variants={fadeUp}
          className="w-full grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          {/* Core Engine */}
          <div className="rounded-xl border border-[var(--bg-border)] bg-[var(--bg-surface)] p-5 hover:border-primary/20 transition-colors duration-300">
            <SectionLabel>Core Engine</SectionLabel>
            <ul className="space-y-3">
              {["Collections & Schema", "Filter Engine", "Automatic Migrations"].map(
                (item) => (
                  <motion.li
                    key={item}
                    variants={scaleIn}
                    className="flex items-center gap-3 text-sm text-[var(--text-secondary)] group cursor-default"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 group-hover:shadow-[0_0_6px_2px_rgba(234,88,12,0.4)] transition-shadow duration-300" />
                    {item}
                  </motion.li>
                )
              )}
            </ul>
          </div>

          {/* Storage Layer */}
          <div className="rounded-xl border border-[var(--bg-border)] bg-[var(--bg-surface)] p-5 hover:border-primary/20 transition-colors duration-300">
            <SectionLabel>Storage Layer</SectionLabel>
            <div className="grid grid-cols-2 gap-3">
              <StorageItem icon={Database} label="SQLite (data.db)" />
              <StorageItem icon={Database} label="SQLite (aux.db)" />
              <StorageItem icon={HardDrive} label="Local Files" />
              <StorageItem icon={Cloud} label="S3 Bucket" />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
